import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Brain,
  Loader2,
  Award,
  TrendingUp,
  BookOpen,
  Target,
  Flame,
  MessageCircle,
  Users,
  Star,
  AlertCircle,
  RefreshCw,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Lightbulb,
  Calendar,
  Clock,
  Trophy,
  Sparkles,
  FileText,
  GraduationCap,
} from "lucide-react";
import { evaluationApi, showSuccess, showError } from "../lib";

interface UserEvaluation {
  _id: string;
  overallScore: number;
  grade: string;
  metrics: {
    coursesCompleted: number;
    totalEnrolled: number;
    completionRate: number;
    certificatesEarned?: number;
    quizzesPassed: number;
    quizzesAttempted: number;
    quizAccuracy: number;
    averageQuizScore: number;
    lessonsCompleted: number;
    totalLessons: number;
    learningStreak: number;
    longestStreak: number;
    totalXP: number;
    badgesEarned: number;
    totalBadges: number;
    forumPosts: number;
    discussionTopics?: number;
    discussionReplies?: number;
    mentorshipSessions: number;
    completedMentorshipSessions?: number;
    mentorshipCompletionRate?: number;
  };
  evaluation: {
    strengths: string[];
    weaknesses: string[];
    learningStyle: string;
    engagementLevel: string;
    consistencyLevel: string;
  };
  recommendations: Array<{
    type: string;
    priority: string;
    title: string;
    description: string;
    actionUrl?: string;
    icon: string;
    completed?: boolean;
  }>;
  insights: {
    bestPerformingCategory?: string;
    worstPerformingCategory?: string;
    peakLearningHour?: number;
    preferredContentType?: string;
    improvementTrend?: string;
  };
  calculatedAt: string;
  nextEvaluationDue?: string;
}

const StudentEvaluationPage = () => {
  const [evaluation, setEvaluation] = useState<UserEvaluation | null>(null);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);

  const fetchEvaluation = async () => {
    try {
      setLoading(true);
      const res = await evaluationApi.getMyEvaluation();
      console.log("📋 Full API response:", res);
      console.log(
        "📋 Certificates in response:",
        res?.data?.metrics?.certificatesEarned,
      );

      if (res?.success && res.data) {
        setEvaluation(res.data);
      }
    } catch (err) {
      console.error("Failed to load evaluation:", err);
    } finally {
      setLoading(false);
    }
  };

  const forceRefreshEvaluation = async () => {
  try {
    setEvaluating(true);
    // First trigger a new evaluation
    const triggerRes = await evaluationApi.triggerMyEvaluation();
    if (triggerRes?.success) {
      // Then fetch the updated evaluation
      const getRes = await evaluationApi.getMyEvaluation();
      if (getRes?.success && getRes.data) {
        console.log('📋 Fresh evaluation:', getRes.data);
        setEvaluation(getRes.data);
        showSuccess("Evaluation refreshed!");
      }
    }
  } catch (err: any) {
    showError(err?.message || "Failed to refresh evaluation");
  } finally {
    setEvaluating(false);
  }
  };
  
  const triggerEvaluation = async () => {
    try {
      setEvaluating(true);
      const res = await evaluationApi.triggerMyEvaluation();
      if (res?.success && res.data) {
        setEvaluation(res.data);
        showSuccess("Evaluation completed successfully!");
      }
    } catch (err: any) {
      showError(err?.message || "Failed to generate evaluation");
    } finally {
      setEvaluating(false);
    }
  };

  useEffect(() => {
    fetchEvaluation();
  }, []);

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case "A":
        return "text-green-600 bg-green-50 border-green-200";
      case "B":
        return "text-blue-600 bg-blue-50 border-blue-200";
      case "C":
        return "text-amber-600 bg-amber-50 border-amber-200";
      case "D":
        return "text-orange-600 bg-orange-50 border-orange-200";
      default:
        return "text-red-600 bg-red-50 border-red-200";
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-blue-600";
    if (score >= 40) return "text-amber-600";
    return "text-red-600";
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 text-red-700 border-red-200";
      case "high":
        return "bg-orange-100 text-orange-700 border-orange-200";
      case "medium":
        return "bg-amber-100 text-amber-700 border-amber-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case "excellent":
        return "text-green-600 bg-green-50";
      case "high":
        return "text-blue-600 bg-blue-50";
      case "consistent":
        return "text-emerald-600 bg-emerald-50";
      case "moderate":
        return "text-amber-600 bg-amber-50";
      case "inconsistent":
        return "text-orange-600 bg-orange-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-3 text-orange-500 animate-spin" />
          <p className="text-xs text-gray-500">Loading evaluation...</p>
        </div>
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
        <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-r from-purple-600 to-orange-500">
                <Brain className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-bold text-gray-900 md:text-xl">
                My Learning Evaluation
              </h1>
            </div>
            <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
              AI-powered insights into your learning performance
            </p>
          </div>

          <div className="py-12 text-center bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
              <Brain className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="mb-1 text-sm font-semibold text-gray-900">
              No evaluation yet
            </h3>
            <p className="text-[10px] text-gray-500 mb-4 max-w-xs mx-auto">
              Generate your first AI-powered learning evaluation to get
              personalized insights and recommendations.
            </p>
            <button
              onClick={triggerEvaluation}
              disabled={evaluating}
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] font-medium text-white rounded-lg bg-gradient-to-r from-purple-600 to-orange-500 hover:shadow-md transition-all disabled:opacity-60"
            >
              {evaluating ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Sparkles className="w-3 h-3" />
              )}
              Generate Evaluation
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-r from-purple-600 to-orange-500">
                <Brain className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-bold text-gray-900 md:text-xl">
                My Learning Evaluation
              </h1>
            </div>
            <button
              onClick={triggerEvaluation}
              disabled={evaluating}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium text-white rounded-lg bg-gradient-to-r from-purple-600 to-orange-500 hover:shadow-md transition-all disabled:opacity-60"
            >
              {evaluating ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              Refresh
            </button>
          </div>
          <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
            Last evaluated:{" "}
            {new Date(evaluation.calculatedAt).toLocaleDateString()}
            {evaluation.nextEvaluationDue &&
              ` · Next: ${new Date(evaluation.nextEvaluationDue).toLocaleDateString()}`}
          </p>
        </div>

        {/* Score Overview */}
        <div className="p-4 mb-5 bg-white border border-gray-100 shadow-sm rounded-xl md:p-6">
          <div className="flex flex-wrap items-center gap-4 md:gap-6">
            <div
              className={`flex items-center justify-center w-20 h-20 rounded-2xl border-2 ${getGradeColor(evaluation.grade)}`}
            >
              <span className="text-2xl font-bold">{evaluation.grade}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="mb-1 text-base font-bold text-gray-900">
                Overall Score:{" "}
                <span className={getScoreColor(evaluation.overallScore)}>
                  {evaluation.overallScore}/100
                </span>
              </h2>
              <div className="h-2 mb-2 overflow-hidden bg-gray-100 rounded-full">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-purple-600 to-orange-500"
                  style={{ width: `${evaluation.overallScore}%` }}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-medium ${getLevelColor(evaluation.evaluation.engagementLevel)}`}
                >
                  <Users className="w-3 h-3" />{" "}
                  {evaluation.evaluation.engagementLevel} engagement
                </span>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-medium ${getLevelColor(evaluation.evaluation.consistencyLevel)}`}
                >
                  <Flame className="w-3 h-3" />{" "}
                  {evaluation.evaluation.consistencyLevel} consistency
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-medium bg-purple-50 text-purple-700">
                  <Brain className="w-3 h-3" />{" "}
                  {evaluation.evaluation.learningStyle} learner
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-3 mb-5 md:gap-4 md:grid-cols-3 lg:grid-cols-4">
          <MetricCard
            icon={<GraduationCap className="w-4 h-4" />}
            label="Courses Completed"
            value={`${evaluation.metrics.coursesCompleted}/${evaluation.metrics.totalEnrolled}`}
            sub={`${evaluation.metrics.completionRate}% rate`}
          />
          <MetricCard
            icon={<CheckCircle className="w-4 h-4" />}
            label="Quizzes Passed"
            value={String(evaluation.metrics.quizzesPassed)}
            sub={`${evaluation.metrics.quizAccuracy}% accuracy`}
          />
          <MetricCard
            icon={<Flame className="w-4 h-4" />}
            label="Learning Streak"
            value={`${evaluation.metrics.learningStreak} days`}
            sub={`Best: ${evaluation.metrics.longestStreak}`}
          />
          <MetricCard
            icon={<Trophy className="w-4 h-4" />}
            label="Total XP"
            value={evaluation.metrics.totalXP.toLocaleString()}
            sub={`${evaluation.metrics.badgesEarned} badges`}
          />
          <MetricCard
            icon={<MessageCircle className="w-4 h-4" />}
            label="Forum Posts"
            value={String(evaluation.metrics.forumPosts)}
            sub={
              evaluation.metrics.discussionTopics
                ? `${evaluation.metrics.discussionTopics} topics, ${evaluation.metrics.discussionReplies} replies`
                : "Community engagement"
            }
          />
          <MetricCard
            icon={<Users className="w-4 h-4" />}
            label="Mentorship"
            value={String(evaluation.metrics.mentorshipSessions)}
            sub={
              evaluation.metrics.completedMentorshipSessions
                ? `${evaluation.metrics.completedMentorshipSessions} completed`
                : "Sessions"
            }
          />
          <MetricCard
            icon={<BookOpen className="w-4 h-4" />}
            label="Lessons"
            value={`${evaluation.metrics.lessonsCompleted}/${evaluation.metrics.totalLessons}`}
            sub="Completed"
          />
          <MetricCard
            icon={<Star className="w-4 h-4" />}
            label="Certificates"
            value={String(evaluation.metrics.certificatesEarned || 0)}
            sub="Earned"
          />
        </div>

        {/* Certificates Earned */}
        {evaluation.metrics.certificatesEarned !== undefined &&
          evaluation.metrics.certificatesEarned > 0 && (
            <div className="p-4 mb-5 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 shadow-sm rounded-xl">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100">
                  <Award className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs font-bold text-emerald-900">
                    Certificates Earned
                  </p>
                  <p className="text-[10px] text-emerald-700">
                    You've earned {evaluation.metrics.certificatesEarned}{" "}
                    certificate
                    {evaluation.metrics.certificatesEarned > 1 ? "s" : ""}! Keep
                    up the great work.
                  </p>
                </div>
              </div>
            </div>
          )}

        {/* Strengths & Weaknesses */}
        <div className="grid grid-cols-1 gap-4 mb-5 md:grid-cols-2">
          <div className="p-4 bg-white border border-gray-100 shadow-sm rounded-xl">
            <h3 className="flex items-center gap-2 mb-3 text-xs font-bold text-gray-900">
              <CheckCircle className="w-4 h-4 text-green-500" /> Strengths
            </h3>
            <div className="space-y-2">
              {evaluation.evaluation.strengths.length > 0 ? (
                evaluation.evaluation.strengths.map((strength, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 p-2 border border-green-100 rounded-lg bg-green-50"
                  >
                    <CheckCircle className="w-3.5 h-3.5 text-green-600 mt-0.5 shrink-0" />
                    <p className="text-[10px] text-gray-700">{strength}</p>
                  </div>
                ))
              ) : (
                <p className="text-[10px] text-gray-500 italic">
                  No strengths identified yet. Keep learning!
                </p>
              )}
            </div>
          </div>
          <div className="p-4 bg-white border border-gray-100 shadow-sm rounded-xl">
            <h3 className="flex items-center gap-2 mb-3 text-xs font-bold text-gray-900">
              <AlertCircle className="w-4 h-4 text-amber-500" /> Areas to
              Improve
            </h3>
            <div className="space-y-2">
              {evaluation.evaluation.weaknesses.length > 0 ? (
                evaluation.evaluation.weaknesses.map((weakness, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 p-2 border rounded-lg bg-amber-50 border-amber-100"
                  >
                    <AlertCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-[10px] text-gray-700">{weakness}</p>
                  </div>
                ))
              ) : (
                <p className="text-[10px] text-gray-500 italic">
                  You're doing great! No major weaknesses identified.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="p-4 mb-5 bg-white border border-gray-100 shadow-sm rounded-xl md:p-6">
          <h3 className="flex items-center gap-2 mb-4 text-xs font-bold text-gray-900">
            <Lightbulb className="w-4 h-4 text-amber-500" /> Personalized
            Recommendations
          </h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {evaluation.recommendations.map((rec, i) => (
              <div
                key={i}
                className="p-3 border border-gray-100 rounded-xl bg-gray-50/50 hover:shadow-sm transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="text-xl">{rec.icon || "💡"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="text-[11px] font-bold text-gray-900">
                        {rec.title}
                      </p>
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-[8px] font-medium ${getPriorityColor(rec.priority)}`}
                      >
                        {rec.priority}
                      </span>
                    </div>
                    <p className="text-[9px] text-gray-600 leading-relaxed">
                      {rec.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Insights */}
        {evaluation.insights &&
          (evaluation.insights.bestPerformingCategory ||
            evaluation.insights.worstPerformingCategory) && (
            <div className="p-4 bg-white border border-gray-100 shadow-sm rounded-xl md:p-6">
              <h3 className="flex items-center gap-2 mb-4 text-xs font-bold text-gray-900">
                <TrendingUp className="w-4 h-4 text-blue-500" /> Learning
                Insights
              </h3>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
                {evaluation.insights.bestPerformingCategory && (
                  <div className="p-3 text-center border border-gray-100 rounded-lg bg-gray-50">
                    <p className="text-[9px] text-gray-500 mb-1">
                      Best Category
                    </p>
                    <p className="text-[10px] font-bold text-gray-900">
                      {evaluation.insights.bestPerformingCategory}
                    </p>
                  </div>
                )}
                {evaluation.insights.worstPerformingCategory && (
                  <div className="p-3 text-center border border-gray-100 rounded-lg bg-gray-50">
                    <p className="text-[9px] text-gray-500 mb-1">Needs Work</p>
                    <p className="text-[10px] font-bold text-gray-900">
                      {evaluation.insights.worstPerformingCategory}
                    </p>
                  </div>
                )}
                {evaluation.insights.peakLearningHour !== undefined && (
                  <div className="p-3 text-center border border-gray-100 rounded-lg bg-gray-50">
                    <p className="text-[9px] text-gray-500 mb-1">Peak Hour</p>
                    <p className="text-[10px] font-bold text-gray-900">
                      {evaluation.insights.peakLearningHour}:00
                    </p>
                  </div>
                )}
                {evaluation.insights.preferredContentType && (
                  <div className="p-3 text-center border border-gray-100 rounded-lg bg-gray-50">
                    <p className="text-[9px] text-gray-500 mb-1">
                      Content Type
                    </p>
                    <p className="text-[10px] font-bold text-gray-900 capitalize">
                      {evaluation.insights.preferredContentType}
                    </p>
                  </div>
                )}
                {evaluation.insights.improvementTrend && (
                  <div className="p-3 text-center border border-gray-100 rounded-lg bg-gray-50">
                    <p className="text-[9px] text-gray-500 mb-1">Trend</p>
                    <p className="text-[10px] font-bold text-gray-900 capitalize">
                      {evaluation.insights.improvementTrend}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

const MetricCard = ({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) => (
  <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl md:p-4">
    <div className="flex items-center gap-2 mb-2">
      <div className="flex items-center justify-center w-8 h-8 text-purple-700 rounded-lg bg-gradient-to-r from-purple-100 to-orange-100">
        {icon}
      </div>
      <p className="text-[9px] text-gray-500">{label}</p>
    </div>
    <p className="text-sm font-bold text-gray-900">{value}</p>
    {sub && <p className="text-[9px] text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

export default StudentEvaluationPage;
